import { tryLoadAndStartRecorder } from '@alwaysmeticulous/recorder-loader'
import { datadogRum } from '@datadog/browser-rum'
import { ThemeProvider as MuiThemeProvider } from '@mui/material'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AlertContextProvider } from '@traba/context'
import { FIVE_MINUTES_IN_MS, ONE_MINUTE_IN_MS } from '@traba/consts'
import { firebaseConfig, FirebaseProvider } from '@traba/firebase-utils'
import { ErrorBoundary } from '@traba/react-components'
import { theme } from '@traba/theme'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { FirebaseAppProvider } from 'reactfire'

import { App } from './App'
import { ErrorBoundaryFallback } from './components/ErrorBoundaryFallback'
import { AppContextProvider } from './context/appContext/AppContext'
import { AuthProvider } from './providers/AuthProvider'
import { UserContextProvider } from './context/user/UserContext'
import { DemoModeProvider } from './demo-mode/DemoModeContext'
import { setupStaleBuildReload } from './setupStaleBuildReload'
import { showBootErrorScreen } from './showBootErrorScreen'
import { setEnvFavicon } from './utils/setEnvFavicon'

declare global {
  interface Window {
    analytics?: {
      identify: (userId: string, traits?: Record<string, unknown>) => void
      track: (event: string, properties?: Record<string, unknown>) => void
      page: (
        category?: string,
        name?: string,
        properties?: Record<string, unknown>,
      ) => void
    }
    Meticulous?: {
      isRunningAsTest: boolean
    }
  }
}

const ddApplicationId = import.meta.env.VITE_DD_APPLICATION_ID
const ddClientToken = import.meta.env.VITE_DD_CLIENT_TOKEN
const ENVIRONMENTS_WITH_METICULOUS = ['development']

async function startApp() {
  if (ENVIRONMENTS_WITH_METICULOUS.includes(import.meta.env.VITE_ENV)) {
    // Note from Meticulous docs: all errors are caught and logged, so no need to surround with try/catch
    await tryLoadAndStartRecorder({
      projectId: import.meta.env.VITE_METICULOUS_PROJECT_ID,
      isProduction: false,
    })
  }

  setupStaleBuildReload()
  setEnvFavicon()

  const datadogEnabled =
    Boolean(ddApplicationId && ddClientToken) &&
    !window.Meticulous?.isRunningAsTest
  if (datadogEnabled) {
    datadogRum.init({
      applicationId: ddApplicationId,
      clientToken: ddClientToken,
      site: 'us5.datadoghq.com',
      service: 'traba-neo-platform',
      env:
        import.meta.env.VITE_ENV === 'production'
          ? 'production'
          : 'development',
      version: import.meta.env.VITE_DEPLOY_VERSION ?? 'local',
      sessionSampleRate: 100,
      premiumSampleRate: 100,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'allow',
    })
  }

  if (
    import.meta.env.VITE_SENTRY_DSN &&
    import.meta.env.VITE_SENTRY_DISABLED !== 'true' &&
    !window.Meticulous?.isRunningAsTest
  ) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration()],
      debug: false,
      environment:
        import.meta.env.VITE_ENV === 'production'
          ? 'production'
          : 'development',
    })
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: FIVE_MINUTES_IN_MS,
        gcTime: 30 * ONE_MINUTE_IN_MS,
        refetchOnWindowFocus: false,
        retry: 2,
      },
    },
  })
  const rootElement = document.getElementById('root')
  if (!rootElement) {
    throw new Error('Root element not found')
  }
  const root = createRoot(rootElement)

  const router = createBrowserRouter([
    {
      path: '*',
      element: (
        <AuthProvider>
          <ErrorBoundary fallbackComponent={ErrorBoundaryFallback}>
            <App />
          </ErrorBoundary>
        </AuthProvider>
      ),
    },
  ])

  root.render(
    <React.StrictMode>
      <MuiThemeProvider theme={theme}>
        <FirebaseAppProvider firebaseConfig={firebaseConfig}>
          <FirebaseProvider>
            <QueryClientProvider client={queryClient}>
              <UserContextProvider>
                <DemoModeProvider>
                  <AppContextProvider>
                    <AlertContextProvider>
                      <RouterProvider router={router} />
                    </AlertContextProvider>
                  </AppContextProvider>
                </DemoModeProvider>
              </UserContextProvider>
            </QueryClientProvider>
          </FirebaseProvider>
        </FirebaseAppProvider>
      </MuiThemeProvider>
    </React.StrictMode>,
  )

  // The session replay recorder is heavy; start it after first paint so it
  // does not compete with the initial React render. RUM + Sentry stay above
  // so early errors and load timing are still captured.
  if (datadogEnabled) {
    const startReplay = () => datadogRum.startSessionReplayRecording()
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(startReplay)
    } else {
      window.setTimeout(startReplay, 1)
    }
  }
}

startApp().catch((error: unknown) => {
  Sentry.captureException(error)
  showBootErrorScreen()
})
